<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">RFM 分析</h1>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="filter-group">
        <label>RFM 分群</label>
        <select v-model="filters.rfmSegment" class="form-control" @change="fetchData">
          <option value="">全部分群</option>
          <option value="champions">冠軍客戶</option>
          <option value="loyal_customers">忠誠客戶</option>
          <option value="potential_loyalists">潛力忠誠客戶</option>
          <option value="new_customers">新客戶</option>
          <option value="promising">有潛力客戶</option>
          <option value="needs_attention">需要關注</option>
          <option value="about_to_sleep">即將沉睡</option>
          <option value="cannot_lose_them">不能失去</option>
          <option value="at_risk">有風險</option>
          <option value="hibernating">休眠客戶</option>
          <option value="lost">流失客戶</option>
        </select>
      </div>
      <div class="filter-group">
        <label>分析日期</label>
        <input
          v-model="filters.analysisDate"
          type="date"
          class="form-control"
          @change="fetchData"
        />
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <div class="error-actions">
        <button class="btn btn-secondary" @click="fetchData">重試</button>
        <button v-if="error.includes('沒有資料') || error.includes('取得資料失敗')" class="btn btn-primary" @click="triggerCalculation">
          <i class="ri-refresh-line"></i>
          執行 RFM 計算
        </button>
      </div>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>會員 ID</th>
            <th>R 分數</th>
            <th>F 分數</th>
            <th>M 分數</th>
            <th>RFM 分數</th>
            <th>分群</th>
            <th>總消費金額</th>
            <th>購買次數</th>
            <th>最後購買日期</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="items.length === 0">
            <td colspan="9" class="empty-state">沒有找到資料</td>
          </tr>
          <tr v-for="item in items" :key="item.id">
            <td>{{ item.memberId }}</td>
            <td>{{ item.rScore }}</td>
            <td>{{ item.fScore }}</td>
            <td>{{ item.mScore }}</td>
            <td>{{ item.rfmScore }}</td>
            <td>
              <span :class="getSegmentBadgeClass(item.rfmSegment)">
                {{ getSegmentDisplayName(item.rfmSegment) }}
              </span>
            </td>
            <td>{{ formatCurrency(item.totalSpent) }}</td>
            <td>{{ item.totalPurchaseCount }}</td>
            <td>{{ formatDate(item.lastPurchaseDate) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="pagination"
      :page="pagination.page"
      :total-pages="pagination.totalPages"
      @change="goToPage"
    />
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { analyticsService } from '@/api/modules/analytics.js';
import { showError, showSuccess } from '@/utils/swal.js';

export default {
  name: 'RfmAnalysis',
  setup() {
    const loading = ref(false);
    const error = ref(null);
    const items = ref([]);
    const pagination = ref(null);
    const filters = ref({
      rfmSegment: '',
      analysisDate: ''
    });

    const fetchData = async () => {
      try {
        loading.value = true;
        error.value = null;
        // 過濾空值，避免傳送空字串
        const params = {
          page: pagination.value?.page || 1,
          pageSize: 20
        };
        if (filters.value.rfmSegment && filters.value.rfmSegment.trim() !== '') {
          params.rfmSegment = filters.value.rfmSegment;
        }
        if (filters.value.analysisDate && filters.value.analysisDate.trim() !== '') {
          params.analysisDate = filters.value.analysisDate;
        }
        const response = await analyticsService.getRfmAnalysis(params);
        if (response.success) {
          items.value = response.data.items || [];
          pagination.value = response.data.pagination;
        } else {
          error.value = response.message || '取得資料失敗';
        }
      } catch (err) {
        console.error('RFM 分析錯誤:', err);
        console.error('錯誤詳情:', {
          response: err.response,
          data: err.response?.data,
          status: err.response?.status,
          message: err.message
        });
        // 詳細錯誤日誌
        const errorDetails = {
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          message: err.message,
          stack: err.stack
        };
        console.error('RFM 分析完整錯誤資訊:', errorDetails);
        
        // 顯示友善的錯誤訊息
        let errorMessage = '取得資料失敗';
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        // 如果是 404 或空資料，顯示更友善的訊息
        if (err.response?.status === 404 || errorMessage.includes('沒有資料') || errorMessage.includes('找不到')) {
          errorMessage = '目前沒有 RFM 分析資料，請先執行 RFM 計算';
        }
        
        error.value = errorMessage;
        showError('錯誤', errorMessage);
      } finally {
        loading.value = false;
      }
    };

    const goToPage = (page) => {
      if (pagination.value) {
        pagination.value.page = page;
        fetchData();
      }
    };

    const formatCurrency = (value) => {
      if (!value) return '$0';
      return `$${parseInt(value).toLocaleString()}`;
    };

    const formatDate = (date) => {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('zh-TW');
    };

    const getSegmentDisplayName = (segment) => {
      if (!segment) return '-';
      const segmentMap = {
        'champions': '冠軍客戶',
        'loyal_customers': '忠誠客戶',
        'potential_loyalists': '潛力忠誠客戶',
        'new_customers': '新客戶',
        'promising': '有潛力客戶',
        'needs_attention': '需要關注',
        'about_to_sleep': '即將沉睡',
        'cannot_lose_them': '不能失去',
        'at_risk': '有風險',
        'hibernating': '休眠客戶',
        'lost': '流失客戶'
      };
      return segmentMap[segment] || segment;
    };

    const getSegmentBadgeClass = (segment) => {
      if (!segment) return 'badge badge-secondary';
      const colorMap = {
        'champions': 'badge badge-success',           // 綠色 - 最佳客戶
        'loyal_customers': 'badge badge-primary',     // 藍色 - 忠誠客戶
        'potential_loyalists': 'badge badge-info',    // 淺藍色 - 潛力客戶
        'new_customers': 'badge badge-warning',      // 黃色 - 新客戶
        'promising': 'badge badge-info',              // 淺藍色 - 有潛力
        'needs_attention': 'badge badge-warning',    // 黃色 - 需要關注
        'about_to_sleep': 'badge badge-warning',      // 黃色 - 即將沉睡
        'cannot_lose_them': 'badge badge-danger',     // 紅色 - 不能失去
        'at_risk': 'badge badge-danger',             // 紅色 - 有風險
        'hibernating': 'badge badge-secondary',       // 灰色 - 休眠
        'lost': 'badge badge-dark'                    // 深灰色 - 流失
      };
      return colorMap[segment] || 'badge badge-secondary';
    };

    const triggerCalculation = async () => {
      try {
        loading.value = true;
        error.value = null;
        const response = await analyticsService.triggerRfmCalculation();
        if (response.success) {
          const data = response.data;
          let successMessage = data.message || 'RFM 計算完成';
          
          // 顯示詳細資訊
          if (data.totalMembers > 0) {
            successMessage += `\n\n統計資訊：\n- 找到 ${data.totalMembers} 位有訂單的會員\n- 成功計算 ${data.successCount} 位\n- 失敗 ${data.failCount} 位`;
            if (data.errors && data.errors.length > 0) {
              successMessage += `\n\n失敗原因：\n${data.errors.map(e => `- 會員 ${e.memberId}: ${e.error}`).join('\n')}`;
            }
          } else {
            successMessage = '目前沒有會員有訂單記錄，無法進行 RFM 計算。請先建立訂單資料。';
          }
          
          showSuccess('成功', successMessage);
          // 等待 2 秒後重新載入資料
          setTimeout(() => {
            fetchData();
          }, 2000);
        } else {
          error.value = response.message || 'RFM 計算失敗';
          showError('錯誤', error.value);
        }
      } catch (err) {
        console.error('RFM 計算錯誤:', err);
        error.value = err.response?.data?.message || err.message || 'RFM 計算失敗';
        showError('錯誤', error.value);
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      fetchData();
    });

    return {
      loading,
      error,
      items,
      pagination,
      filters,
      fetchData,
      goToPage,
      formatCurrency,
      formatDate,
      triggerCalculation,
      getSegmentDisplayName,
      getSegmentBadgeClass
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';
</style>
